/* eslint-disable @typescript-eslint/ban-ts-comment */

import type { ClientHandle, CombinedError, Operation } from "@urql/vue";
import { get, set } from "idb-keyval";
import { ref, watchEffect } from "vue";
import { useLogger } from "@/composables/logger";

const { error } = useLogger("CONFLICT_REVISION_COMPOSABLE");

interface OriginalDiff {
  [key: string]: { new: any; old: any };
}

interface NormalizedDiff {
  new: { [key: string]: any };
  old: { [key: string]: any };
}

interface SqlError {
  error: string;
  table: string;
  current_winning_revision_id: string;
  incoming_revision_id: string;
  diff: OriginalDiff;
}

interface RevisionConflict {
  operation: Operation;
  diff?: NormalizedDiff;
}

interface Extension {
  [key: string]: { error: any };
}

const revisionConflicts = ref<RevisionConflict[]>([]);

// get revisionConflicts from IDB on page load
// and update idb whenever revisionConflictes updates
get<RevisionConflict[]>("revisionConflicts").then(savedConflicts => {
  if (savedConflicts) {
    revisionConflicts.value = savedConflicts;
  }
});
watchEffect(() => {
  set("revisionConflicts", JSON.parse(JSON.stringify(revisionConflicts.value)));
});

function journalIdByConflict(conflict: RevisionConflict): string | undefined {
  return conflict.operation.variables?.object?.journal_id;
}

function findConflictIndex(conflict: RevisionConflict) {
  return revisionConflicts.value.findIndex(
    savedConflict =>
      // @ts-ignore
      journalIdByConflict(savedConflict) === journalIdByConflict(conflict)
  );
}

function sqlErrorObject(error: CombinedError): SqlError | undefined {
  return JSON.parse(
    (
      error.graphQLErrors[0].extensions?.internal as {
        error: {
          message: string;
        };
      }
    )?.error?.message
  );
}

function currentWinningRevisionIdByError(
  err: CombinedError
): string | undefined {
  const error = sqlErrorObject(err);
  return error?.current_winning_revision_id;
}

function createNormalizedDiff(err: CombinedError): NormalizedDiff | undefined {
  const originalDiff = sqlErrorObject(err)?.diff;

  if (!originalDiff) {
    error("Diff not found on operation", err);
    return;
  }

  return Object.entries(originalDiff).reduce(
    (acc, [key, value]) => {
      acc.new[key] = value.new;
      acc.old[key] = value.old;
      return acc;
    },
    { new: {}, old: {} } as NormalizedDiff
  );
}

export function useConflictRevisions({ handle }: { handle?: ClientHandle }) {
  // run in onError exchange
  function insertOrUpdateRevisionConflict(
    operation: Operation,
    opError: CombinedError
  ) {
    // create normalized diff
    const diff = createNormalizedDiff(opError);
    const newConflict: RevisionConflict = { operation, diff };

    // swap out parent_revision_id
    const winning_revision_id = currentWinningRevisionIdByError(opError);

    // !insertScenarioMutation
    let conflictingVariableName = "object";

    // insertScenarioMutation
    if (newConflict.operation.variables) {
      if (!newConflict.operation.variables.object) {
        const internalError = opError.graphQLErrors[0].extensions as Extension;
        const extensionError = internalError.internal.error;
        const revisionTable: string = JSON.parse(extensionError.message).table;
        conflictingVariableName = revisionTable.split(".").pop() as string;
      }

      newConflict.operation.variables[
        conflictingVariableName
      ][0].parent_revision_id = winning_revision_id;

      // find index of existing entry by journal_id
      const index = findConflictIndex(newConflict);

      // replace or append conflict to list
      if (index > -1) {
        revisionConflicts.value[index] = newConflict;
      } else {
        revisionConflicts.value.push(newConflict);
      }
    }
  }

  function fastForwardRevision(conflict: RevisionConflict): void {
    const index = findConflictIndex(conflict);

    if (index > -1) {
      const operation = revisionConflicts.value[index].operation as Operation;

      // refire operation

      if (handle) {
        handle.client.reexecuteOperation(operation);
      }

      revisionConflicts.value.splice(index, 1);
    }
  }

  function discardRevision(conflict: RevisionConflict): void {
    const index = findConflictIndex(conflict);

    if (index > -1) {
      revisionConflicts.value.splice(index, 1);
    }
  }

  return {
    insertOrUpdateRevisionConflict,
    discardRevision,
    fastForwardRevision,
    revisionConflicts
  };
}
